import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Send, AlertCircle, CheckCheck, Clock, MailOpen, ArrowDownLeft, ArrowUpRight, User, Briefcase, FileText, Receipt } from "lucide-react";
import EmailComposeDialog from "@/components/EmailComposeDialog";

/**
 * EmailThread — reusable chronological email history panel.
 *
 * When BOTH `customerId` and a per-record id are passed, we render a toggle
 * (This <record> / Customer thread) with the record view as the default so
 * replies land where the work lives. Pages without a record id (Customer
 * profile) get a single customer-scoped list with no toggle.
 */
export default function EmailThread({
  customerId = null,
  jobId = null,
  invoiceId = null,
  estimateId = null,
  prefillTo = "",
  title = "Email thread",
  testIdPrefix = "email-thread",
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const listRef = useRef(null);

  const recordKind = jobId ? "job" : invoiceId ? "invoice" : estimateId ? "estimate" : null;
  const recordId = jobId || invoiceId || estimateId || null;
  const toggleAvailable = !!(customerId && recordId);

  // When a per-record id exists we default to record scope (where the work
  // lives). Customer-only pages have no toggle so mode stays "customer".
  const [mode, setMode] = useState(toggleAvailable ? "record" : "customer");
  // Re-sync when caller swaps the record/customer id (e.g. after routing).
  useEffect(() => {
    setMode(toggleAvailable ? "record" : (customerId ? "customer" : "record"));
  }, [toggleAvailable, customerId]);

  const scope = useMemo(() => {
    if (mode === "customer" && customerId) return { customer_id: customerId };
    if (recordKind === "job") return { job_id: jobId };
    if (recordKind === "invoice") return { invoice_id: invoiceId };
    if (recordKind === "estimate") return { estimate_id: estimateId };
    if (customerId) return { customer_id: customerId };
    return null;
  }, [mode, customerId, recordKind, jobId, invoiceId, estimateId]);

  const load = useCallback(async (scrollToEnd = false) => {
    if (!scope) { setLoading(false); return; }
    try {
      const params = new URLSearchParams(scope).toString();
      const { data } = await api.get(`/emails/thread?${params}`);
      setMessages(data?.messages || []);
      if (scrollToEnd) {
        setTimeout(() => {
          const el = listRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 60);
      }
    } catch {
      setMessages([]);
    } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { setLoading(true); load(true); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(false), 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!scope) return null;

  const recordMeta = {
    job: { label: "This job", Icon: Briefcase },
    invoice: { label: "This invoice", Icon: Receipt },
    estimate: { label: "This estimate", Icon: FileText },
  }[recordKind || "job"];

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp" data-testid={testIdPrefix}>
      <div className="p-5 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Email thread</div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-sky-600"/>
            <span className="font-heading text-lg font-bold">{title}</span>
            <span className="text-xs text-zinc-500 font-mono-accent">· {messages.length} {messages.length === 1 ? "message" : "messages"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {toggleAvailable && recordMeta && (
            <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-xs" data-testid={`${testIdPrefix}-scope-toggle`}>
              <button
                type="button"
                onClick={() => setMode("record")}
                className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${mode === "record" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`${testIdPrefix}-scope-record`}
              >
                <recordMeta.Icon className="w-3.5 h-3.5"/>{recordMeta.label}
              </button>
              <button
                type="button"
                onClick={() => setMode("customer")}
                className={`px-3 py-1.5 inline-flex items-center gap-1.5 border-l border-zinc-200 ${mode === "customer" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`${testIdPrefix}-scope-customer`}
              >
                <User className="w-3.5 h-3.5"/>Customer thread
              </button>
            </div>
          )}
          <Button
            size="sm"
            onClick={() => setComposeOpen(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid={`${testIdPrefix}-compose-btn`}
          >
            <Send className="w-3.5 h-3.5 mr-1"/> Compose
          </Button>
        </div>
      </div>

      <div ref={listRef} className="p-5 space-y-3 max-h-[520px] overflow-y-auto bg-zinc-50/40" data-testid={`${testIdPrefix}-list`}>
        {loading && (
          <div className="text-sm text-zinc-500 text-center py-8">Loading email history…</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-10" data-testid={`${testIdPrefix}-empty`}>
            <Mail className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-sm text-zinc-500">
              {toggleAvailable && mode === "record"
                ? <>No emails tied to this {recordKind} yet. Try <button onClick={() => setMode("customer")} className="text-sky-600 hover:underline">Customer thread</button> or <strong>Compose</strong> a new one.</>
                : <>No emails yet. Click <strong>Compose</strong> to send the first one.</>
              }
            </div>
          </div>
        )}
        {!loading && messages.map((m, i, arr) => {
          const prev = arr[i - 1];
          const showDay = !prev || dayLabel(m.created_at) !== dayLabel(prev.created_at);
          return (
            <div key={m.id}>
              {showDay && (
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-zinc-200"/>
                  <span className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-400">{dayLabel(m.created_at)}</span>
                  <div className="flex-1 h-px bg-zinc-200"/>
                </div>
              )}
              <EmailBubble
                msg={m}
                expanded={expandedId === m.id}
                onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                testIdPrefix={testIdPrefix}
              />
            </div>
          );
        })}
      </div>

      <EmailComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        customerId={customerId}
        jobId={jobId}
        invoiceId={invoiceId}
        estimateId={estimateId}
        prefillTo={prefillTo}
        onSent={() => load(true)}
      />
    </div>
  );
}


function EmailBubble({ msg, expanded, onToggle, testIdPrefix }) {
  const isOut = msg.direction === "outbound";
  const Dir = isOut ? ArrowUpRight : ArrowDownLeft;
  const dirLabel = isOut ? "Sent" : "Received";

  const StatusIcon = msg.status === "received"
    ? MailOpen
    : msg.status === "failed"
      ? AlertCircle
      : msg.status === "sent"
        ? CheckCheck
        : Clock;
  const statusColor = msg.status === "failed"
    ? "text-rose-600"
    : msg.status === "sent" || msg.status === "received"
      ? "text-emerald-600"
      : "text-amber-600";

  const bubbleCls = isOut
    ? "bg-sky-50 border border-sky-200 text-zinc-900"
    : "bg-white border border-zinc-200 text-zinc-900";

  const bodyPreview = (msg.body || "").slice(0, 240);
  const needsExpand = (msg.body || "").length > 240;

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[86%] ${isOut ? "text-right" : "text-left"}`}>
        <div className={`inline-flex items-center gap-1.5 text-[9px] font-mono-accent uppercase tracking-widest mb-1 ${isOut ? "flex-row-reverse" : ""}`}>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${isOut ? "bg-sky-100 text-sky-800" : "bg-zinc-100 text-zinc-600"}`}>
            <Dir className="w-3 h-3"/>
            {dirLabel}
            <span className="text-zinc-500">· {isOut ? (msg.to_email || "no recipient") : (msg.from_email || "unknown sender")}</span>
          </span>
        </div>
        <div className={`inline-block rounded-xl px-4 py-3 text-sm shadow-sm ${bubbleCls}`} data-testid={`${testIdPrefix}-bubble-${msg.id}`}>
          {msg.subject && (
            <div className="font-semibold text-zinc-900 mb-1.5 text-sm text-left">{msg.subject}</div>
          )}
          <div className="whitespace-pre-wrap break-words text-left text-zinc-800">
            {expanded || !needsExpand ? msg.body : bodyPreview + "…"}
          </div>
          {needsExpand && (
            <button
              type="button"
              onClick={onToggle}
              className="mt-2 text-[10px] font-mono-accent uppercase tracking-widest text-sky-700 hover:underline"
              data-testid={`${testIdPrefix}-toggle-${msg.id}`}
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
        <div className={`text-[10px] font-mono-accent text-zinc-500 mt-1 flex items-center gap-1.5 ${isOut ? "justify-end" : "justify-start"}`}>
          <span>{fmtTime(msg.created_at)}</span>
          <span>·</span>
          <span className={`inline-flex items-center gap-0.5 ${statusColor}`}>
            <StatusIcon className="w-2.5 h-2.5"/>{(msg.status || "sent").toUpperCase()}
          </span>
          {msg.mocked && isOut && <span className="text-amber-600 uppercase">TEST</span>}
          {msg.sent_by && isOut && <span className="text-zinc-400">· by {msg.sent_by}</span>}
        </div>
      </div>
    </div>
  );
}


function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, now)) return "Today";
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
